import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { getUserData } from '../services/storageService';
import { 
  getMonthlySummary, 
  getWeeklySummary,
  getCustomRangeSummary 
} from '../services/livestreamService';
import Sidebar from './common/Sidebar';

const Reports = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const user = getUserData();
  const [reportType, setReportType] = useState('month');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchReport();
  }, [user, navigate, reportType, selectedDate]);

  const fetchReport = async () => {
    try {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      const week = Math.ceil((selectedDate.getDate() + new Date(year, selectedDate.getMonth(), 1).getDay()) / 7);

      let response;
      if (reportType === 'month') {
        response = await getMonthlySummary(user.userId, year, month);
      } else if (reportType === 'week') {
        response = await getWeeklySummary(user.userId, year, week);
      }

      if (response?.success) {
        setReportData(response.data);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Sidebar user={user}>
        <Container maxWidth="xl">
          <Card sx={{ 
            mb: 4,
            borderRadius: 3,
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}>
            <Box sx={{ 
              p: 3, 
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
            }}>
              <AssessmentIcon sx={{ color: '#1a237e', mr: 2, fontSize: 28 }} />
              <Typography variant="h5" sx={{ color: '#1a237e' }}>
                Báo cáo thống kê
              </Typography>
            </Box>
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Loại báo cáo</InputLabel>
                    <Select
                      value={reportType}
                      label="Loại báo cáo"
                      onChange={(e) => setReportType(e.target.value)}
                      sx={{
                        borderRadius: 2,
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#1a237e',
                          },
                        },
                      }}
                    >
                      <MenuItem value="month">Theo tháng</MenuItem>
                      <MenuItem value="week">Theo tuần</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label={reportType === 'month' ? "Chọn tháng" : "Chọn tuần"}
                    value={selectedDate}
                    onChange={(newValue) => setSelectedDate(newValue)}
                    views={reportType === 'month' ? ['year', 'month'] : undefined}
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        sx: { 
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': {
                              borderColor: '#1a237e',
                            },
                          },
                        }
                      } 
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                height: '100%',
                background: 'linear-gradient(45deg, #1a237e 30%, #534bae 90%)',
                borderRadius: 3,
                boxShadow: '0 8px 16px rgba(26,35,126,0.2)',
              }}>
                <CardContent>
                  <Stack spacing={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccessTimeIcon sx={{ fontSize: 40, color: 'white', opacity: 0.9 }} />
                      <Typography variant="h6" sx={{ ml: 2, color: 'white' }}>
                        Tổng số giờ
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ color: 'white' }}>
                      {reportData?.totalHours || 0}
                    </Typography>
                    <Box>
                      <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                        {reportType === 'month' ? 'Mục tiêu: 160 giờ' : 'Mục tiêu: 40 giờ'}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min((reportData?.totalHours || 0) / (reportType === 'month' ? 160 : 40) * 100, 100)}
                        sx={{ 
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'rgba(255,255,255,0.2)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'white',
                            borderRadius: 4,
                          }
                        }}
                      />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ 
                height: '100%',
                borderRadius: 3,
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#1a237e', mb: 3 }}>
                    Chi tiết thống kê
                  </Typography>
                  <Stack spacing={3}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: '#f5f8fa', 
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      <CalendarMonthIcon sx={{ color: '#1a237e', mr: 2 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Số ngày đã làm việc
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#1a237e' }}>
                          {reportData?.records?.length || 0} ngày
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: '#f5f8fa', 
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      <TrendingUpIcon sx={{ color: '#1a237e', mr: 2 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Trung bình giờ/ngày
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#1a237e' }}>
                          {reportData?.records?.length 
                            ? (reportData.totalHours / reportData.records.length).toFixed(1) 
                            : 0} giờ
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Sidebar>
    </LocalizationProvider>
  );
};

export default Reports; 